import {
    ActionCreatorWithPayload, CaseReducer, createAsyncThunk,
} from '@reduxjs/toolkit'
import {
    NinchatClientPromiseValue, NinchatEventActionPayload,
} from 'api/NinchatClient'
import {
    NinchatEventType, NinchatTranscriptUpdatedEvent,
} from '../types/events'
import {
    extractProperties,
    isEmpty,
} from '.'
import {
    push,
    replace,
} from 'navigation/actions'
import {NinchatState} from 'api/types'
import {RootState} from 'store'
import router from 'navigation/router'

const eventTypePrefix = 'NINCHAT/event'

type TranscriptUpdatedAsyncThunk = typeof thunk & {reducer: CaseReducer<NinchatState>} & TranscriptUpdatedActionCreator

type TranscriptUpdatedActionCreator = ActionCreatorWithPayload<NinchatEventActionPayload<NinchatTranscriptUpdatedEvent>, 'NINCHAT/event/transcript_updated'>

type TranscriptUpdatedAsyncThunkConfig = {
    state: RootState
}

const thunk = createAsyncThunk<NinchatClientPromiseValue, NinchatTranscriptUpdatedEvent, TranscriptUpdatedAsyncThunkConfig>(
    `${eventTypePrefix}/${NinchatEventType.NinchatTranscriptUpdatedEvent}`,
    (event, thunkAPI) => {
        const {
            audience_id,
            assign_user_id,
            queue_id,
            transcript_status,
        } = event

        const {
            position,
            routes,
        } = thunkAPI.getState().navigation

        const {
            user_id, user_queues, _queues,
        } = thunkAPI.getState().ninchat

        const groupId = Object.entries({
            ...user_queues,
            ..._queues,
        }).find(([qId]) => qId === queue_id)?.[1]?.queue_attrs?.group_id

        const route = routes[position]

        if (transcript_status === 'done' || !assign_user_id) {
            if (router.isRoute<'RegisteredAudience'>('RegisteredAudience', route) && route.params.audience_id === audience_id) {
                thunkAPI.dispatch(replace(queue_id
                    ? {
                        name: 'Queue',
                        params: {queue_id: groupId ? groupId : queue_id},
                    }
                    : {name: 'Home'}))
                return event
            }
        }

        if (assign_user_id === user_id) {
            thunkAPI.dispatch(push({
                name: 'RegisteredAudience',
                params: {
                    audience_id,
                    queue_id,
                },
            }))
        }

        return event
    },
)

export const transcriptUpdated = thunk as TranscriptUpdatedAsyncThunk

transcriptUpdated.reducer = (state, action) => {
    const {
        audience_id,
        queue_id,
        realm_id,
        complete_time,
        assign_user_id,
        transcript_status,
        ...rest
    } = extractProperties<NinchatTranscriptUpdatedEvent>(action.payload)

    isEmpty(rest)

    if (transcript_status === 'done' || !assign_user_id) {
        // Remove from sidebar if I set transcript assigned to me as "done" / unassign
        delete state._user_audience_transcripts[audience_id]
    }

    if (complete_time && queue_id && realm_id) {
        if (transcript_status !== 'done' && assign_user_id === state.user_id) {
            // Assigned to me: move from _audience_transcripts to _user_audience_transcripts
            delete state._audience_transcripts[audience_id]

            state._user_audience_transcripts[audience_id] = {
                assign_user_id,
                audience_id,
                complete_time,
                queue_id,
                realm_id,
                transcript_status,
            }
        }

        if (assign_user_id !== state.user_id) {
            state._audience_transcripts[audience_id] = {
                assign_user_id,
                audience_id,
                complete_time,
                queue_id,
                realm_id,
                transcript_status,
            }
        }
    }
}
